import { forwardRef, useImperativeHandle, useRef } from 'react';
import { cn } from '@/lib/utils';
import { HighlightedText } from './highlighted-text';
import type { FlatMatch } from '@/lib/use-in-page-find';

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** Match relativi a QUESTA textarea (offset sul `value`). */
  matches: FlatMatch[];
  /** Indice globale del match corrente (per lo stile `data-current`). */
  currentIndex: number;
  className?: string;
  id?: string;
  rows?: number;
  required?: boolean;
  maxLength?: number;
};

/**
 * Textarea con evidenziazione visibile dei match. Dietro alla textarea (sfondo
 * trasparente) un "backdrop" rende lo stesso testo con `<mark>` (testo
 * trasparente: si vedono solo gli sfondi gialli/arancio dei match), sincronizzato
 * in scroll. Le stesse classi di box + `scrollbar-gutter: stable` su entrambi
 * mantengono identico il word-wrap, così gli sfondi cadono sotto le parole giuste.
 */
export const HighlightTextarea = forwardRef<HTMLTextAreaElement, Props>(function HighlightTextarea(
  { value, onChange, matches, currentIndex, className, id, rows, required, maxLength },
  ref,
) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => taRef.current as HTMLTextAreaElement, []);

  function syncScroll() {
    const ta = taRef.current;
    const bd = backdropRef.current;
    if (ta && bd) {
      bd.scrollTop = ta.scrollTop;
      bd.scrollLeft = ta.scrollLeft;
    }
  }

  return (
    <div className="relative w-full">
      <div
        ref={backdropRef}
        aria-hidden
        style={{ scrollbarGutter: 'stable' }}
        className={cn(
          'find-backdrop',
          className,
          'pointer-events-none absolute inset-0 m-0 block w-full select-none resize-none overflow-hidden whitespace-pre-wrap border-transparent bg-transparent text-transparent',
        )}
      >
        <HighlightedText text={value} matches={matches} currentIndex={currentIndex} />
      </div>
      <textarea
        id={id}
        ref={taRef}
        required={required}
        rows={rows}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={syncScroll}
        style={{ scrollbarGutter: 'stable' }}
        className={cn(className, 'relative block w-full bg-transparent')}
      />
    </div>
  );
});
